import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Avatar, Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material'

import SecuritySearchModal from './SecuritySearchModal'
import { prefetchSecurityCategories } from '../../services/apis/securities'
import { LOGOS_BASE_URL, ViewType } from '../../assets/constants'
import { getSecuritiesFromSecuritiesObj, getSecurityKey, isFnOSecurity } from '../../util/security'
import { ExpressionCallbackData } from './typings'
import { convertExpressionToDisplayExpression, isExpressionMode } from '../../util/expression'
import { selectRecentExpressions } from '../../selectors/common'
import commonActions from '../../actions/common.actions'
import { RecentExpression } from '../../reducer/common.reducer'
import { getColorForSymbol } from '../../util/general'
import { selectIsPrimarySecurityLoading, selectPrimarySecurity, selectSecuritySearchOpen } from '../../selectors/home'
import SearchIcon from '../../assets/icons/SearchIcon'
import { getStrategyIconFromText } from '../../util/strategyBuilder'
import { setSecuritySearchOpen, updatePrimarySecurity } from '../../reducer/home.reducer'
import { updatePrimarySecurityAsync } from '../../reducer/home.thunks'
import { generatePath, useNavigate } from 'react-router-dom'
import ROUTES from '../../assets/routes'
import { exchangeToJSON } from '../../proto/generated/common'
import useSecurityExpressionToName from '../../hooks/useExpressionNames'
import { ActionType, useUndoRedoContext } from '../UndoRedoFunctonalities/UndoRedo.context'
import { SecurityCategory_CategoryCode } from '../../proto/generated/security'
import { useUser } from '../../context/UserContext'
import { FeatureCategory, FeatureName } from '../adminUserRestrictions/Restrictions.constants'
import { setFeatureRestrictionDialog } from '../../reducer/user.reducer'
import { FeatureCode } from '../../util/RestrictionFeatureCode'
import { AppDispatch } from '../../store'
import SubtleShimmer from '../common/SubtleShimmer'

import Chart from '../../assets/icons/Chart'
import FinancialData from '../../assets/icons/FinancialData'
import OptionChain from '../../assets/icons/OptionChain'
import OptionWizard from '../../assets/icons/OptionWizard'
import ScalperIcon from '../../assets/icons/ScalperIcon'
import IvPlots from '../../assets/icons/IvPlots'
import BackTestingIcon from '../../assets/icons/BackTestingIcon'
import StrategyBuilder from '../../assets/icons/StrategyBuilder'
import SeasonalsIcon from '../../assets/icons/SeasonalsIcon'
import TechnicalsIcon from '../../assets/icons/TechnicalsIcon'
import HeatmapIcon from '../../assets/icons/HeatmapIcon'
import IndexComponentsIcon from '../../assets/icons/IndexComponentsIcon'
import useSelectPrimaryOrUnderlyingSecurity from '../../hooks/useSelectPrimaryOrUnderlyingSecurity'
import useSecurityViewSelect from '../../hooks/useSecurityViewSelect'
import { SecurityViewMenu } from './SecurityViewMenu'
import { NonInteractiveTooltip } from '../custom'

export type SecuritySearchProps = {
    view: ViewType
    isOnlyIndexSecurity?: boolean
    hideExpression?: boolean
    showIndexDropDown: boolean
}

const SecuritySearch: React.FC<SecuritySearchProps> = ({ view, showIndexDropDown, isOnlyIndexSecurity = false, hideExpression = false }) => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const { push } = useUndoRedoContext()
    const { key, primary, underlying, underlyingSecurityCode, expressionMode, expressionSecurities, displayName } = useSelector(selectPrimarySecurity)
    const isSecurityLoading = useSelector(selectIsPrimarySecurityLoading)
    const recentExpressions = useSelector(selectRecentExpressions)
    const [securitiesModalOpen, setSecuritiesModalOpen] = useState<boolean>(false)
    const securitySearchOpen = useSelector(selectSecuritySearchOpen)
    const expressionToName = useSecurityExpressionToName()
    const [mouseEnter, setMouseEnter] = useState<boolean>(false)
    const { primaryOrUnderlying: mainSecurity, } = useSelectPrimaryOrUnderlyingSecurity()
    const initialSecurityRendered = useRef(false)
    const previousSymbolRef = useRef<any>(null)
    const selectedSecurity = useMemo(() => expressionMode ? null : primary && isFnOSecurity(primary) && (view !== ViewType.CHART && view !== ViewType.SYMBOL && view !== ViewType.FINANCIAL_DATA && view !== ViewType.SEASONALS && view !== ViewType.TECHNICALS && view !== ViewType.INDEX_COMPONENTS) ? underlying : primary, [expressionMode, primary, view, underlying])
    const { user: { hasAccess } } = useUser()
    const [viewMenuAnchor, setViewMenuAnchor] = useState<null | HTMLElement>(null)
    const [menuAnchorPosition, setMenuAnchorPosition] = useState<{ top: number; left: number } | null>(null)
    const searchButtonRef = useRef<HTMLButtonElement>(null)
    const iconButtonRef = useRef<HTMLButtonElement>(null)
    const isViewMenuOpen = Boolean(viewMenuAnchor)

    const shouldShowViewMenu = Boolean(mainSecurity) && !expressionMode

    useEffect(() => {
        prefetchSecurityCategories()
    }, [])

    const handleViewMenuClose = useCallback(() => {
        setViewMenuAnchor(null)
        setMenuAnchorPosition(null)
    }, [])

    const onViewSelect = useSecurityViewSelect({
        security: mainSecurity,
        expressionMode,
        onComplete: handleViewMenuClose,
    })

    const renderViewIcon = useCallback(() => {
        const iconStyle = { width: 22, height: 22, color: 'inherit',  strokeWidth: 2 }
        switch (view) {
            case ViewType.CHART:
            case ViewType.SPREAD:
            case ViewType.SYMBOL:
                return <Chart sx={iconStyle} />
            case ViewType.FINANCIAL_DATA:
                return <FinancialData sx={iconStyle} />
            case ViewType.SEASONALS:
                return <SeasonalsIcon sx={iconStyle} />
            case ViewType.TECHNICALS:
                return <TechnicalsIcon sx={iconStyle} />
            case ViewType.INDEX_COMPONENTS:
                return <IndexComponentsIcon sx={iconStyle} />
            case ViewType.HEATMAP:
                return <HeatmapIcon sx={iconStyle} />
            case ViewType.SHAREHOLDER_HOLDINGS_DATA:
                return <SearchIcon sx={iconStyle} />
            case ViewType.OPTION_CHAIN:
                return <OptionChain sx={iconStyle} />
            case ViewType.OPTION_WIZARD:
                return <OptionWizard sx={iconStyle} />
            case ViewType.STRATEGY_BUILDER:
                return <StrategyBuilder sx={iconStyle} />
            case ViewType.SCALPER:
                return <ScalperIcon sx={iconStyle} />
            case ViewType.MULTI_STRIKES_CHART:
            case ViewType.OI_OVERVIEW:
            case ViewType.OPEN_INTEREST:
            case ViewType.OPEN_INTEREST_CHANGE:
                return <IvPlots sx={iconStyle} />
            case ViewType.SIMULATOR:
                return <BackTestingIcon sx={iconStyle} />
            default:
                return null
        }
    }, [view])

    useEffect(() => {
        if (!initialSecurityRendered.current && !!key) {
            previousSymbolRef.current = {
                key: key,
                expressionMode: expressionMode,
                securities: expressionSecurities,
                underlyingSecurityCode: underlyingSecurityCode
            }
            initialSecurityRendered.current = true
        }
    }, [expressionMode, expressionSecurities, key, underlyingSecurityCode, underlying])

    const onSecuritySelected = useCallback(
        async (data: ExpressionCallbackData) => {
            const expressionMode = isExpressionMode(data.expression)
            if (!hasAccess(FeatureCategory.CHART, '', FeatureName.SECURITY_EXPRESSIONS) && expressionMode) {
                dispatch(setFeatureRestrictionDialog({
                    open: true,
                    feature: "Security expressions",
                    featureRestrictionCode: FeatureCode.SECURITY_EXPRESSIONS
                }))
                return
            }
            const securities = await getSecuritiesFromSecuritiesObj(data.securities)
            if (!securities) return
            const securityKey = getSecurityKey(securities[0].exchange, securities[0].code)
            const isFnO = isFnOSecurity(securities[0])
            const currentSecurityData = {
                key: securityKey,
                expressionMode: data.expressionMode,
                securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                underlyingSecurityCode: getSecurityKey(securities[0].exchange, isFnO ? securities[0].underlyingSecurityCode! : securities[0].code)
            }

            const previousSecurityData = previousSymbolRef.current
            // setSymbolDataHistory((prev: any) => {
            //                 const historyData = {
            //                     currentData: currentSecurityData,
            //                     previousData: previousSecurityData
            //                 }
            //                 previousSymbolRef.current = currentSecurityData
            //                 push([historyData], `selected symbol ${currentSecurityData.securities}`, ActionType.SECURITY_CHANGE)
            //                 return [...prev, historyData]
            //             })
            previousSymbolRef.current = currentSecurityData
            push([{
                currentData: currentSecurityData,
                previousData: previousSecurityData
            }], `selected symbol ${currentSecurityData.securities}`, ActionType.SECURITY_CHANGE)

            let updatePrimary = true
            if (securitySearchOpen && securitySearchOpen.open && securitySearchOpen.onSecuritySelected) {
                updatePrimary = false
                if (data.expressionMode) {
                    securitySearchOpen.onSecuritySelected(
                        data.expression,
                        true,
                        securities.map((v) => getSecurityKey(v.exchange, v.code))
                    )
                } else {
                    securitySearchOpen.onSecuritySelected(
                        getSecurityKey(securities[0].exchange, securities[0].code),
                        true,
                        securities.map((v) => getSecurityKey(v.exchange, v.code))
                    )
                }
            }
            setSecuritiesModalOpen(false)
            dispatch(setSecuritySearchOpen({ open: false, key: undefined, isExpression: false }))
            if (!updatePrimary) return
            if (!data.expressionMode) {

                if (view === ViewType.CHART || view === ViewType.SCALPER || view === ViewType.SHAREHOLDER_HOLDINGS_DATA) {
                    updatePrimarySecurityAsync({
                        key: securityKey,
                        expressionMode: data.expressionMode,
                        securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                        underlyingSecurityCode: getSecurityKey(securities[0].exchange, isFnO ? securities[0].underlyingSecurityCode! : securities[0].code)
                    })(dispatch)
                } else if (view === ViewType.FINANCIAL_DATA) {
                    updatePrimarySecurityAsync({
                        key: securityKey,
                        expressionMode: data.expressionMode,
                        securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                        underlyingSecurityCode: getSecurityKey(securities[0].exchange, isFnO ? securities[0].underlyingSecurityCode! : securities[0].code)
                    })(dispatch)
                    navigate(
                        generatePath(ROUTES.financials, {
                            exchange: exchangeToJSON(securities[0].exchange),
                            symbol: securities[0].code
                        })
                    )
                } else if (view === ViewType.SEASONALS || view === ViewType.TECHNICALS || view === ViewType.INDEX_COMPONENTS) {
                    updatePrimarySecurityAsync({
                        key: securityKey,
                        expressionMode: data.expressionMode,
                        securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                        underlyingSecurityCode: getSecurityKey(securities[0].exchange, isFnO ? securities[0].underlyingSecurityCode! : securities[0].code)
                    })(dispatch)
                    const route = view === ViewType.SEASONALS
                        ? ROUTES.seasonals
                        : view === ViewType.TECHNICALS
                            ? ROUTES.technicals
                            : ROUTES.components
                    navigate(
                        generatePath(route, {
                            exchange: exchangeToJSON(securities[0].exchange),
                            symbol: securities[0].code
                        })
                    )
                } else if ([ViewType.SYMBOL, ViewType.SPREAD].includes(view)) {
                    navigate(generatePath(ROUTES.symbol, { symbol: securityKey }))
                } else if (view === ViewType.OPTION_CHAIN) {
                    updatePrimarySecurityAsync({
                        key: securityKey,
                        expressionMode: data.expressionMode,
                        securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                        underlyingSecurityCode: getSecurityKey(securities[0].exchange, isFnO ? securities[0].underlyingSecurityCode! : securities[0].code)
                    })(dispatch)
                    navigate(
                        generatePath(ROUTES.optionChain, {
                            exchange: exchangeToJSON(securities[0].exchange),
                            symbol: securities[0].code
                        })
                    )
                } else if (view === ViewType.OPTION_WIZARD) {
                    updatePrimarySecurityAsync({
                        key: securityKey,
                        expressionMode: data.expressionMode,
                        securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                        underlyingSecurityCode: getSecurityKey(securities[0].exchange, isFnO ? securities[0].underlyingSecurityCode! : securities[0].code)
                    })(dispatch)
                    navigate(
                        generatePath(ROUTES.optionWizard, {
                            exchange: exchangeToJSON(securities[0].exchange),
                            symbol: securities[0].code
                        })
                    )
                } else if (view === ViewType.STRATEGY_BUILDER) {
                    dispatch(
                        updatePrimarySecurity({
                            key: securityKey,
                            expressionMode: data.expressionMode,
                            securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                            underlyingSecurityCode: getSecurityKey(securities[0].exchange, isFnO ? securities[0].underlyingSecurityCode! : securities[0].code)
                        })
                    )
                    navigate(
                        generatePath(ROUTES.strategyBuilder, {
                            exchange: exchangeToJSON(securities[0].exchange),
                            symbol: securities[0].code
                        })
                    )
                } else if (
                    view === ViewType.OI_OVERVIEW ||
                    view === ViewType.OPEN_INTEREST ||
                    view === ViewType.OPEN_INTEREST_CHANGE
                ) {
                    const oiRoute =
                        view === ViewType.OI_OVERVIEW
                            ? ROUTES.oiOverview
                            : view === ViewType.OPEN_INTEREST
                              ? ROUTES.openInterest
                              : ROUTES.openInterestChange
                    dispatch(
                        updatePrimarySecurity({
                            key: securityKey,
                            expressionMode: data.expressionMode,
                            securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                            underlyingSecurityCode: getSecurityKey(securities[0].exchange, isFnO ? securities[0].underlyingSecurityCode! : securities[0].code)
                        })
                    )
                    navigate(
                        generatePath(oiRoute, {
                            exchange: exchangeToJSON(securities[0].exchange),
                            symbol: securities[0].code
                        })
                    )
                } else if (view === ViewType.MULTI_STRIKES_CHART) {
                    updatePrimarySecurityAsync({
                        key: securityKey,
                        expressionMode: data.expressionMode,
                        securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                        underlyingSecurityCode: getSecurityKey(securities[0].exchange, isFnO ? securities[0].underlyingSecurityCode! : securities[0].code)
                    })(dispatch)
                    navigate(
                        generatePath(ROUTES.multiStrikeChart, {
                            exchange: exchangeToJSON(securities[0].exchange),
                            symbol: securities[0].code
                        })
                    )
                } else if (view === ViewType.SIMULATOR) {
                    dispatch(
                        updatePrimarySecurity({
                            key: securityKey,
                            expressionMode: data.expressionMode,
                            securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                            underlyingSecurityCode: getSecurityKey(securities[0].exchange, isFnO ? securities[0].underlyingSecurityCode! : securities[0].code)
                        })
                    )
                    navigate(
                        generatePath(ROUTES.simulator, {
                            exchange: exchangeToJSON(securities[0].exchange),
                            symbol: securities[0].code
                        })
                    )
                } else if (view === ViewType.AUTOMATION) {
                    dispatch(
                        updatePrimarySecurity({
                            key: securityKey,
                            expressionMode: data.expressionMode,
                            securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                            underlyingSecurityCode: getSecurityKey(securities[0].exchange, isFnO ? securities[0].underlyingSecurityCode! : securities[0].code)
                        })
                    )
                    navigate(
                        generatePath(ROUTES.automation, {
                            exchange: exchangeToJSON(securities[0].exchange),
                            symbol: securities[0].code
                        })
                    )
                }
            } else if (data.expression && data.securities) {
                if (data.type === 'EXPRESSION') {
                    commonActions.saveRecentExpressions(dispatch, [
                        ...recentExpressions.filter((exp: RecentExpression) => exp.expression !== data.expression),
                        {
                            expression: data.expression,
                            id: data.expressionId ?? data.expression,
                            securities: securities,
                            lastUsedTime: new Date().getTime()
                        }
                    ] as RecentExpression[])
                } else if (data.type === 'STRATEGY_EXPRESSION') {
                    commonActions.updateStrategyExpressionMetaData(dispatch, {
                        name: data.name ?? convertExpressionToDisplayExpression(data.expression, securities),
                        expression: data.expression
                    })
                }

                if ([ViewType.SYMBOL, ViewType.SPREAD].includes(view)) {
                    navigate(generatePath(ROUTES.spread, { spread: data.expression }))
                } else {
                    updatePrimarySecurityAsync({
                        key: data.expression,
                        expressionMode: true,
                        displayName: data.name,
                        securities: securities.map((v) => getSecurityKey(v.exchange, v.code)),
                        underlyingSecurityCode: getSecurityKey(securities[0].exchange, securities[0].hasFo ? securities[0].code! : (securities[0].underlyingSecurityCode ?? ''))
                    })(dispatch)
                }
            }
        },
        [dispatch, securitySearchOpen, push, view, navigate, recentExpressions, hasAccess]
    )

    const onMouseEnter = useCallback(() => {
        setMouseEnter(true)
    }, [])

    const onMouseLeave = useCallback(() => {
        setMouseEnter(false)
    }, [])

    const displaySymbol = useMemo(() => {
        if (!expressionMode) {
            return (selectedSecurity?.symbol || selectedSecurity?.code?.toUpperCase().split('_').join(' ') || '')
        }
        if (displayName && typeof displayName === 'string') {
            return displayName
        }
        try {
            return convertExpressionToDisplayExpression(key, expressionSecurities) || ''
        } catch {
            return ''
        }
    }, [expressionMode, selectedSecurity, key, expressionSecurities, expressionToName, displayName])

    const displayText = useMemo(() => {
        if (!expressionMode) {
            return selectedSecurity?.displayName || ''
        }
        try {
            return convertExpressionToDisplayExpression(key, expressionSecurities) || ''
        } catch {
            return ''
        }
    }, [expressionMode, expressionSecurities, selectedSecurity, key])

    const tooltipContent = useMemo(
        () => (
            <div>
                {!expressionMode && <Typography variant="body1">{typeof selectedSecurity?.symbol === 'string' ? selectedSecurity.symbol : ''}</Typography>}
                <Typography variant="caption" className="security-sub-header" sx={{ color: (theme) => theme.palette.text.secondary }}>
                    {typeof displayText === 'string' ? displayText : ''}
                </Typography>
            </div>
        ),
        [displayText, expressionMode, selectedSecurity]
    )

    const logo = useMemo(() => {
        if (!expressionMode || !displayName) return undefined
        return getStrategyIconFromText(displayName);
    }, [expressionMode, key, displayName])

    const searchButton = useMemo(
        () => (
            <Button
                component="div"
                ref={searchButtonRef}
                disabled={!displaySymbol}
                disableRipple
                color="inherit"
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                onClick={(e) => {
                    e.stopPropagation()
                    prefetchSecurityCategories()
                    setSecuritiesModalOpen(true)
                }}
                sx={{
                    borderRadius: '15px',
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    padding: '4px 4px',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : '#f6f6f6',
                    '&:hover': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
                    },
                    '& .MuiTypography-root': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                    },
                    minWidth: 'max-content !important'
                }}
                data-event={'Search Clicked'}
                data-event-source={'stockbar'}
            >
                <Stack direction={'row'} spacing={1} alignItems={'center'} justifyContent="flex-start" sx={{ minWidth: 80, width: 80 }}>
                    {!displaySymbol ? (
                        <SubtleShimmer loading>
                            <Box sx={{ height: 20, width: 80, borderRadius: 0.5, }} />
                        </SubtleShimmer>
                    ) : (
                        <NonInteractiveTooltip title={"Symbol Search"} arrow placement='bottom'>
                            <Typography noWrap variant="body2" sx={{ fontWeight: 500, maxWidth: 80, width: 80, textAlign: 'left', fontSize: '0.875rem', pl: 0.5 }}>
                                {typeof displaySymbol === 'string' ? displaySymbol : ''}
                            </Typography>
                        </NonInteractiveTooltip>
                    )}
                </Stack>

                <IconButton
                    ref={iconButtonRef}
                    disableRipple
                    id="side-bar-left-buttons"
                    data-event-source={'left-side-bar'}
                    onClick={(e) => {
                        e.stopPropagation()
                        if (isViewMenuOpen) {
                            handleViewMenuClose()
                            return
                        }
                        if (searchButtonRef.current && iconButtonRef.current) {
                            const searchRect = searchButtonRef.current.getBoundingClientRect()
                            const iconRect = iconButtonRef.current.getBoundingClientRect()
                            setMenuAnchorPosition({
                                top: searchRect.bottom,
                                left: iconRect.left - 16
                            })
                        }
                        setViewMenuAnchor(iconButtonRef.current)
                        setSecuritiesModalOpen(false)
                    }}
                    sx={{
                      ml: 0.5,
                        borderRadius: '50%',
                        padding: 0,
                        minWidth: 'auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.9)' : '#ffffff',
                        '&:hover': {
                            backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
                        }
                    }}
                >
                    {renderViewIcon()}
                </IconButton>

            </Button>
        ),
        [
            onMouseEnter,
            onMouseLeave,
            displaySymbol,
            expressionMode,
            logo,
            selectedSecurity?.logoUrl,
            selectedSecurity?.symbol,
            selectedSecurity?.code,
            displayText,
            isViewMenuOpen,
            handleViewMenuClose,
            renderViewIcon
        ]
    )

    return useMemo(
        () => (
            <>
                <span style={{ display: 'inline-flex' }}>
                    {searchButton}
                </span>

                {shouldShowViewMenu && (
                    <SecurityViewMenu
                        open={isViewMenuOpen}
                        onClose={handleViewMenuClose}
                        anchorReference="anchorPosition"
                        anchorPosition={menuAnchorPosition ?? { top: 0, left: 0 }}
                        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        clickAwayIgnoreRefs={[iconButtonRef, searchButtonRef]}
                        id="side-bar-left-buttons"
                        dataEventSource="left-side-bar"
                        view={view}
                        onViewSelect={onViewSelect}
                        security={mainSecurity}
                    />
                )}

                {(securitiesModalOpen || securitySearchOpen.open) && (
                    <SecuritySearchModal
                        showOnlySimulatorSecurities={isOnlyIndexSecurity}
                        showOnlyFOSecurities={[
                            ViewType.SCALPER,
                            ViewType.OPTION_CHAIN,
                            ViewType.OPTION_WIZARD,
                            ViewType.STRATEGY_BUILDER,
                            ViewType.MULTI_STRIKES_CHART,
                            ViewType.OI_OVERVIEW,
                            ViewType.OPEN_INTEREST,
                            ViewType.OPEN_INTEREST_CHANGE,
                        ].includes(view)}
                        open={securitiesModalOpen || securitySearchOpen.open}
                        enableExpressions={![ViewType.SCALPER, ViewType.OPTION_CHAIN, ViewType.OPTION_WIZARD, ViewType.STRATEGY_BUILDER, ViewType.MULTI_STRIKES_CHART, ViewType.OI_OVERVIEW, ViewType.OPEN_INTEREST, ViewType.OPEN_INTEREST_CHANGE].includes(view)}
                        deleteIcon={true}
                        onCloseClicked={() => {
                            setSecuritiesModalOpen(false)
                            dispatch(
                                setSecuritySearchOpen({
                                    open: false,
                                    key: undefined,
                                    isExpression: false,
                                    onSecuritySelected: () => { }
                                })
                            )
                        }}
                        onSecuritySelected={onSecuritySelected}
                        {...(hideExpression && {
                            isHideExpression: true
                        })}
                        {...(isOnlyIndexSecurity && {
                            forceCategoryCode: SecurityCategory_CategoryCode.INDEX
                        })}
                    />
                )}
            </>
        ),
        [tooltipContent, mouseEnter, searchButton, securitiesModalOpen, securitySearchOpen.open, view, isOnlyIndexSecurity, hideExpression, onSecuritySelected, dispatch, shouldShowViewMenu, renderViewIcon, isViewMenuOpen, menuAnchorPosition, handleViewMenuClose, onViewSelect, mainSecurity]
    )

}

export default React.memo(SecuritySearch)
